import {
  DraftBendingTextConfiguration,
  DraftConfiguration,
  DraftState,
  DraftTextConfiguration,
} from '@/types';
import { getBendingTextSize } from '@/utils/text/getBendingTextSize';
import { getTextSizeForStyle } from '@/utils/text/getTextSizeForStyle';
import { invertAngle } from '@/utils/text/invertAngle';
import { getCurrentSizeOfConfiguration } from '@/model/configuration/getCurrentSizeOfConfiguration';
import { getMaxScaleForConfiguration } from '@/model/configuration/getMaxScale';
import { getScaleAndFontSize } from '@/model/configuration/getScaleAndFontSize';
import {
  isDraftBendingTextConfiguration,
  isDraftTextConfiguration,
} from '@/model/configuration/isDraftConfigurationType';
import { getConfiguration } from '@/model/draft/getConfiguration';
import { adjustTextStyleOffset } from './helper/adjustTextStyleOffset';
import { getTextStyleForWidth } from './helper/getTextStyleForWidth';
import { updateSizeAndOffset } from './helper/updateSizeAndOffset';

const convertTextConfigurationToBendingText = (
  draft: DraftState,
  configuration: DraftTextConfiguration,
  angle: number
) => {
  const currentText = configuration.text;
  const textStyle = currentText.style;
  const fontSize = textStyle.fontSize;

  const newText = {
    content: currentText.content,
    style: {
      font: textStyle.font,
      textAlign: textStyle.textAlign,
      fontSize: 25,
      angle,
      x: 0,
      y: 0,
    },
  };

  const draftBendingTextConfiguration: DraftBendingTextConfiguration = {
    ...configuration,
    type: 'bendingText',
    text: newText,
  };

  updateSizeAndOffset(
    draftBendingTextConfiguration,
    getBendingTextSize(newText),
    Math.min(fontSize / 25, getMaxScaleForConfiguration(draftBendingTextConfiguration))
  );

  draft.configurations[draftBendingTextConfiguration.cid] = draftBendingTextConfiguration;
};

const convertBendingTextConfigurationToText = (
  draft: DraftState,
  configuration: DraftBendingTextConfiguration
) => {
  const currentSize = getCurrentSizeOfConfiguration(configuration);

  const textContent = configuration.text.content;
  const textStyle = getTextStyleForWidth(
    configuration.text.content,
    currentSize.width,
    configuration.text.style.font,
    configuration.printType,
    configuration.text.style.textAlign
  );

  const draftTextConfiguration: DraftTextConfiguration = {
    ...configuration,
    type: 'text',
    text: {
      content: textContent,
      style: textStyle,
    },
  };

  let newSize = getTextSizeForStyle(textStyle, textContent);
  const { fontSize: fontSizeLimitedToPrintArea } = getScaleAndFontSize(
    textStyle,
    draftTextConfiguration.rotation,
    newSize,
    draftTextConfiguration.printArea,
    draftTextConfiguration.printType,
    1
  );
  if (fontSizeLimitedToPrintArea !== textStyle.fontSize) {
    textStyle.fontSize = fontSizeLimitedToPrintArea;
    adjustTextStyleOffset(textStyle, textContent);
    newSize = getTextSizeForStyle(textStyle, textContent);
  }

  updateSizeAndOffset(draftTextConfiguration, newSize, 1);

  draft.configurations[draftTextConfiguration.cid] = draftTextConfiguration;
};

export const setTextAngleAction =
  ({ cid }: DraftConfiguration, angle: number) =>
  (draft: DraftState) => {
    const configuration = getConfiguration(draft, cid);
    const invertedAngle = invertAngle(angle);

    if (isDraftTextConfiguration(configuration)) {
      if (Math.abs(angle) > 18) {
        convertTextConfigurationToBendingText(draft, configuration, invertedAngle);
        return;
      }
    }

    if (!isDraftBendingTextConfiguration(configuration)) {
      return;
    }

    if (Math.abs(angle) <= 18) {
      convertBendingTextConfigurationToText(draft, configuration);
      return;
    }

    const text = configuration.text;
    const textStyle = text.style;

    textStyle.angle = invertedAngle;

    updateSizeAndOffset(
      configuration,
      getBendingTextSize(text),
      Math.min(configuration.scale, getMaxScaleForConfiguration(configuration))
    );
  };
